/* TimeHistoryBookModal — "Min historiebok" — list + read past simulator journeys */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, BookOpen, Loader2, Trash2, ChevronRight, ArrowLeft, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import { shareOrDownload } from './journeyShareCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('sv-SE', { day: 'numeric', month: 'short', year: 'numeric' }) +
           ' · ' + d.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

function JourneyDetail({ id, onBack }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/timefeed/journey/${id}`, { withCredentials: true });
        setData(r.data);
      } catch (e) {
        toast.error('Kunde inte ladda resan');
      } finally { setLoading(false); }
    })();
  }, [id]);

  const handleShare = async () => {
    if (!data || sharing) return;
    setSharing(true);
    try {
      const coverDataUrl = data.clip_image_base64
        ? `data:${data.clip_image_mime || 'image/png'};base64,${data.clip_image_base64}`
        : null;
      const r = await shareOrDownload(data, coverDataUrl);
      if (r?.shared) toast.success('📲 Delad 💜');
      else if (r?.downloaded) toast.success('📥 Bilden sparad — redo att dela 💜');
    } catch (e) {
      toast.error('Kunde inte skapa bild: ' + (e?.message || ''));
    } finally {
      setSharing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center text-white">
        <Loader2 className="w-8 h-8 animate-spin text-violet-300" />
      </div>
    );
  }
  if (!data) return null;

  const bg = data.clip_image_base64
    ? `url("data:${data.clip_image_mime || 'image/png'};base64,${data.clip_image_base64}")`
    : 'linear-gradient(135deg,#3D1A78,#7C5FCC)';

  return (
    <div className="flex-1 overflow-y-auto" data-testid="journey-detail">
      <div
        className="relative h-44 sm:h-56 flex items-end p-4 text-white"
        style={{ backgroundImage: bg, backgroundSize: 'cover', backgroundPosition: 'center' }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/40 to-black/85 pointer-events-none" />
        <button
          onClick={onBack}
          data-testid="journey-back"
          className="absolute top-3 left-3 z-10 px-2.5 py-1.5 rounded-xl bg-black/55 hover:bg-black/75 text-white text-xs font-bold flex items-center gap-1.5 backdrop-blur"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Tillbaka
        </button>
        <button
          onClick={handleShare}
          disabled={sharing}
          data-testid="journey-share"
          className="absolute top-3 right-3 z-10 px-2.5 py-1.5 rounded-xl bg-violet-600/85 hover:bg-violet-600 text-white text-xs font-bold flex items-center gap-1.5 backdrop-blur disabled:opacity-50"
        >
          {sharing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Share2 className="w-3.5 h-3.5" />}
          {sharing ? 'Skapar bild…' : '🎬 Dela'}
        </button>
        <div className="relative z-10">
          {data.role_label && <p className="text-[10px] uppercase tracking-widest font-extrabold text-violet-300">{data.role_label}</p>}
          <h2 className="font-black text-xl leading-tight drop-shadow">{data.clip_hook}</h2>
          <p className="text-[11px] italic opacity-80">{data.clip_year} · {data.clip_place}</p>
        </div>
      </div>

      <div className="px-4 py-4 space-y-3 bg-gradient-to-b from-violet-950 to-black text-white">
        <p className="text-[10px] uppercase tracking-widest font-bold text-violet-300">
          📜 Din resa · {data.scene_count} scener · {fmtDate(data.created_at)}
        </p>
        {(data.scenes || []).map((s, i) => (
          <div key={i} className="rounded-2xl bg-black/40 border border-violet-500/30 px-3 py-2.5" data-testid={`journey-scene-${i}`}>
            <p className="text-[10px] font-extrabold uppercase tracking-widest text-violet-300/80">Scen {i + 1}{s.is_pivotal ? ' · vändpunkt' : ''}</p>
            <p className="text-sm leading-relaxed font-serif mt-1">{s.narration}</p>
            {s.choice && (
              <p className="text-[12px] italic text-amber-200 mt-1.5 pl-2 border-l-2 border-amber-400/50">
                → Du valde: <span className="font-bold not-italic">{s.choice}</span>
              </p>
            )}
          </div>
        ))}
        {data.final_narration && (
          <div className="rounded-2xl bg-gradient-to-r from-rose-700/40 to-violet-700/40 border border-amber-300/40 px-3 py-2.5">
            <p className="text-[10px] uppercase tracking-widest font-extrabold text-amber-200">Slutet</p>
            <p className="text-sm italic mt-1 font-serif">{data.final_narration}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default function TimeHistoryBookModal({ onClose }) {
  const [list, setList] = useState(null);
  const [activeId, setActiveId] = useState(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/timefeed/journey/list`, { withCredentials: true });
      setList(r.data?.items || []);
    } catch (e) {
      toast.error('Kunde inte ladda historieboken');
      setList([]);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm('Ta bort denna resa permanent?')) return;
    try {
      await axios.delete(`${API}/timefeed/journey/${id}`, { withCredentials: true });
      setList((l) => (l || []).filter((x) => x.id !== id));
      if (activeId === id) setActiveId(null);
    } catch { toast.error('Kunde inte ta bort'); }
  };

  return (
    <div
      className="fixed inset-0 z-[370] bg-black/90 backdrop-blur-md flex items-center justify-center p-2 sm:p-4"
      onClick={onClose}
      data-testid="historybook-modal"
    >
      <div
        className="relative w-full max-w-2xl rounded-3xl overflow-hidden shadow-2xl border-2 border-violet-400/40 max-h-[94vh] flex flex-col bg-gradient-to-b from-violet-950 to-black"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-4 py-3 bg-gradient-to-r from-violet-900 to-violet-800 flex items-center justify-between text-white border-b border-violet-400/40">
          <div className="flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-amber-300" />
            <div>
              <p className="text-[10px] uppercase tracking-widest text-amber-200 font-bold">Min historiebok</p>
              <h2 className="font-black text-sm sm:text-base">Dina tidsresor 💜</h2>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-white/15" data-testid="historybook-close">
            <X className="w-5 h-5" />
          </button>
        </header>

        {activeId ? (
          <JourneyDetail id={activeId} onBack={() => setActiveId(null)} />
        ) : (
          <div className="flex-1 overflow-y-auto p-4 space-y-2.5">
            {list === null && (
              <div className="flex items-center justify-center py-12 text-violet-300">
                <Loader2 className="w-8 h-8 animate-spin" />
              </div>
            )}
            {list && list.length === 0 && (
              <div className="text-center py-12 text-violet-200">
                <BookOpen className="w-10 h-10 mx-auto mb-3 opacity-50" />
                <p className="font-bold text-sm">Din historiebok är tom 📜</p>
                <p className="text-xs italic opacity-70 mt-1">Spela en simulator och avsluta scenen — så hamnar resan här automatiskt</p>
              </div>
            )}
            {list && list.map((j) => (
              <button
                key={j.id}
                onClick={() => setActiveId(j.id)}
                data-testid={`journey-card-${j.id}`}
                className="group w-full text-left rounded-2xl bg-white/95 hover:bg-white border-2 border-violet-300 hover:border-violet-500 p-3 shadow transition flex items-center gap-3"
              >
                <div className="flex-1 min-w-0">
                  {j.role_label && <p className="text-[9px] uppercase tracking-widest font-extrabold text-violet-700/70">{j.role_label}</p>}
                  <p className="font-black text-sm text-violet-900 truncate">{j.clip_hook}</p>
                  <p className="text-[10px] italic text-gray-600 truncate">{j.clip_year} · {j.clip_place} · {j.scene_count} scener</p>
                  <p className="text-[10px] text-amber-700 mt-0.5">{fmtDate(j.created_at)}</p>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); remove(j.id); }}
                  className="p-1.5 rounded-full hover:bg-rose-100 text-rose-500 opacity-50 hover:opacity-100"
                  data-testid={`journey-delete-${j.id}`}
                  title="Ta bort"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
                <ChevronRight className="w-4 h-4 text-violet-400 group-hover:text-violet-700" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
